import React, { useState, useEffect, useRef } from 'react';
import { aparenciaAPI, API_BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Image as ImageIcon, Upload, RotateCcw, Loader2, Info } from 'lucide-react';
import { LOGIN_BG_PADRAO } from '@/lib/loginImagem';

const AparenciaLogin = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const inputRef = useRef(null);

  const carregar = async () => {
    try {
      const res = await aparenciaAPI.obterLogin();
      setConfig(res.data);
    } catch {
      toast.error('Erro ao carregar configuração');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  const handleArquivo = async (e) => {
    const arquivo = e.target.files?.[0];
    e.target.value = '';
    if (!arquivo) return;
    if (!arquivo.type.startsWith('image/')) return toast.error('Selecione um arquivo de imagem');
    if (arquivo.size > 8 * 1024 * 1024) return toast.error('Imagem muito grande. Máximo: 8MB');
    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append('arquivo', arquivo);
      const res = await aparenciaAPI.enviarImagemLogin(fd);
      setConfig(res.data);
      toast.success('Imagem de login atualizada!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar imagem');
    } finally {
      setEnviando(false);
    }
  };

  const restaurar = async () => {
    if (!window.confirm('Voltar para a imagem padrão do sistema?')) return;
    setEnviando(true);
    try {
      await aparenciaAPI.restaurarImagemLogin();
      setConfig({ imagem_url: null });
      toast.success('Imagem padrão restaurada');
    } catch {
      toast.error('Erro ao restaurar');
    } finally {
      setEnviando(false);
    }
  };

  const previewUrl = config?.imagem_url ? `${API_BASE.replace(/\/api$/, '')}${config.imagem_url}` : LOGIN_BG_PADRAO;

  return (
    <div className="space-y-6" data-testid="aparencia-login-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Imagem de Login</h1>
        <p className="text-gray-500 text-sm mt-1">Troque a imagem de fundo das telas de login, recuperação e troca de senha</p>
      </div>

      <Card className="bg-gray-50 border-gray-200 dark:bg-gray-900/40 dark:border-gray-800">
        <CardContent className="py-4 flex items-start gap-3">
          <Info className="h-5 w-5 text-purple-500 mt-0.5 shrink-0" />
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Use uma imagem em paisagem (ex.: 1920×1080) em JPG, PNG ou WEBP com até 8MB. A alteração vale imediatamente para todos os usuários.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ImageIcon className="h-4 w-4 text-red-600" /> Pré-visualização
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
          ) : (
            <div className="relative rounded-xl overflow-hidden border border-gray-200 dark:border-gray-800 aspect-video max-w-3xl bg-black" data-testid="login-imagem-preview">
              <img src={previewUrl} alt="Fundo da tela de login" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 flex items-center justify-end pr-8">
                <div className="w-44 h-28 rounded-lg" style={{ background: 'rgba(15,15,20,0.65)', border: '1px solid rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }} />
              </div>
              <span className="absolute left-3 bottom-3 text-[11px] px-2 py-1 rounded bg-black/60 text-white" data-testid="login-imagem-status">
                {config?.imagem_url ? `Personalizada • ${config.atualizado_por || ''}` : 'Imagem padrão do sistema'}
              </span>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleArquivo} data-testid="login-imagem-input" />
            <Button onClick={() => inputRef.current?.click()} disabled={enviando} className="bg-red-600 hover:bg-red-700" data-testid="login-imagem-upload-btn">
              {enviando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              Enviar nova imagem
            </Button>
            {config?.imagem_url && (
              <Button variant="outline" onClick={restaurar} disabled={enviando} data-testid="login-imagem-restaurar-btn">
                <RotateCcw className="mr-2 h-4 w-4" /> Restaurar padrão
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AparenciaLogin;
